import { useMutation, useQueryClient } from '@tanstack/react-query';

import { roleService } from '@/services/role-service';
import { pollForResourceGone } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { roleMutationKeys, roleQueryKeys } from './query-keys';

/**
 * Hook to delete a role.
 * Waits until the read model no longer shows the role, then invalidates the cache.
 */
export function useDeleteRole(tenantId?: UUID) {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: roleMutationKeys.deleteRole(tenantId),
    mutationFn: async (roleId: UUID) => {
      if (!tenantId) throw new Error('Missing tenant id');

      await roleService.deleteRole(tenantId, roleId);
      await pollForResourceGone(() => roleService.findRole(tenantId, roleId));
      return roleId;
    },
    onSuccess: async (_, roleId) => {
      await qc.invalidateQueries({
        queryKey: roleQueryKeys.listByTenant(tenantId),
        refetchType: 'all',
      });
      qc.removeQueries({ queryKey: roleQueryKeys.detail(tenantId, roleId) });
    },
  });
}
